import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Suspense, useState } from "react";

import { listProviders, upsertProvider, deleteProvider } from "@/lib/admin-providers.functions";
import { ProviderModuleShell } from "@/components/admin/provider/ProviderModuleShell";
import { SiteHeader } from "@/components/site/SiteHeader";
import { AdminNav } from "@/components/admin/AdminNav";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const providersQuery = queryOptions({
  queryKey: ["admin", "providers"],
  queryFn: () => listProviders(),
});

export const Route = createFileRoute("/_authenticated/admin/providers")({
  head: () => ({
    meta: [{ title: "Proveedores aliados · Mostrando México" }, {name:"description",content:"Administración privada de proveedores aliados."},{property:"og:title",content:"Proveedores aliados · Mostrando México"},{property:"og:description",content:"Administración privada de proveedores aliados."},{property:"og:type",content:"website"},{name:"twitter:card",content:"summary"}, { name: "robots", content: "noindex,nofollow" }],
  }),
  component: ProvidersPage,
});

function ProvidersPage() {
  return (
    <ProviderModuleShell title="Proveedores" description="Operadores aliados y comisiones por defecto."><Body /></ProviderModuleShell>
  );
}

function Body() {
  const { data } = useSuspenseQuery(providersQuery);
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "providers"] });

  const [form, setForm] = useState({
    name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    default_commission_pct: 10,
  });

  const upsert = useMutation({
    mutationFn: () =>
      upsertProvider({
        data: {
          name: form.name,
          ...(form.contact_name ? { contact_name: form.contact_name } : {}),
          ...(form.contact_email ? { contact_email: form.contact_email } : {}),
          ...(form.contact_phone ? { contact_phone: form.contact_phone } : {}),
          default_commission_pct: Number(form.default_commission_pct) || 0,
          active: true,
        },
      }),
    onSuccess: () => {
      setForm({ name: "", contact_name: "", contact_email: "", contact_phone: "", default_commission_pct: 10 });
      invalidate();
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => deleteProvider({ data: { id } }),
    onSuccess: invalidate,
  });

  return (
    <div className="space-y-6">
      <div className="rounded-lg border bg-card p-5">
        <h2 className="text-sm font-semibold">Nuevo proveedor</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Input placeholder="Nombre comercial" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input placeholder="Contacto" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
          <Input placeholder="Email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
          <Input placeholder="Teléfono" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
          <div className="flex gap-2">
            <Input
              type="number"
              placeholder="Comisión %"
              value={form.default_commission_pct}
              onChange={(e) => setForm({ ...form, default_commission_pct: Number(e.target.value) })}
            />
            <Button onClick={() => upsert.mutate()} disabled={!form.name || upsert.isPending}>Crear</Button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Nombre</th>
              <th className="px-3 py-2">Contacto</th>
              <th className="px-3 py-2">Contacto</th>
              <th className="px-3 py-2">Comisión</th>
              <th className="px-3 py-2">Estado</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {data.providers.map((p: any) => (
              <tr key={p.id} className="border-t hover:bg-muted/30">
                <td className="px-3 py-2 font-medium">{p.name}</td>
                <td className="px-3 py-2 text-muted-foreground">{p.contact_name ?? "—"}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  <div>{p.contact_email ?? "—"}</div>
                  <div>{p.contact_phone ?? ""}</div>
                </td>
                <td className="px-3 py-2">{p.default_commission_pct ?? 0}%</td>
                <td className="px-3 py-2">{p.active ? "Activo" : "Inactivo"}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    className="text-xs text-destructive hover:underline"
                    onClick={() => {
                      if (confirm(`¿Eliminar ${p.name}?`)) del.mutate(p.id);
                    }}
                  >
                    Eliminar
                  </button>
                </td>
              </tr>
            ))}
            {!data.providers.length && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-muted-foreground">Sin proveedores.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
